/**
 * Pre-registration lead form (WWW-S023; moved to its own page in WWW-S038).
 *
 * The form's structure, layout, and copy are authored in pre-register/index.html.
 * Salesforce owns only the *dynamic values inside* fields — dropdown options and
 * validation constraints — served by GET /lead/form-metadata. Options are baked
 * into the HTML at build (so the form renders complete with JS disabled) and
 * refreshed here on load; a picklist edited in the org therefore flows to the
 * form with no code change.
 *
 * Submission is JS-driven: the intake is a JSON REST endpoint (POST /lead/intake),
 * so it cannot accept a native form POST. Without JS the page still renders fully
 * and a <noscript> block points to email — see pre-register/index.html. This is the
 * accepted consequence of replacing native Web-to-Lead with the custom endpoint.
 */

import { initPhoneInputs } from "./phone";
import {
  initSteps,
  clearProgress,
  percentagesValid,
  readSteps,
  type StepsController,
} from "./lead-form-steps";
import { HONEYPOT_FIELD, initSubmissionGuard } from "./lead-form-guard";

export interface PicklistOption {
  value: string;
  label: string;
}
export interface LeadFieldMeta {
  key: string;
  apiName: string;
  label: string;
  type: string;
  required: boolean;
  maxLength?: number;
  picklistValues?: PicklistOption[];
}
export interface LeadFormMetadata {
  objectName: string;
  fields: LeadFieldMeta[];
}
export interface IntakeError {
  field?: string;
  message: string;
}
export interface IntakeResult {
  success: boolean;
  statusCode: number;
  leadId?: string;
  submissionId?: string;
  errors?: IntakeError[];
  networkError?: boolean;
}

type FieldControl = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

const CHECKBOX = "checkbox";
const RADIO = "radio";

/**
 * Build the JSON payload from the form, keyed by each control's `name` (the
 * semantic key the intake expects).
 *
 * Rules, each of which exists for a reason:
 * - **Checkboxes send booleans**, so an unchecked consent box is an explicit
 *   `false` rather than a missing key.
 * - **Radio groups send only the CHECKED member.** Every radio in a group shares
 *   a `name`, and `.value` is its own value whether or not it is selected — so
 *   iterating blindly would post the last radio in the group regardless of what
 *   the visitor chose (WWW-S035).
 * - **Disabled controls are skipped**, which is how conditional sections stay out
 *   of the payload. When a section does not apply (mailing address same as
 *   physical, applicant is an owner/officer, no attestation exceptions) it is
 *   hidden AND its controls disabled — see `setConditional` in lead-form-steps.
 *   Without that, a visitor who answered "No", typed a value, then switched back
 *   to "Yes" would still submit the stale value.
 * - **`data-url` fields are normalized to an absolute https:// URL.** Agencies
 *   type "agencyname.com"; Salesforce wants a real URL. Demanding the scheme in
 *   the field was the old `type="url"` behavior and it read as the form being
 *   broken (WWW-S038), so the scheme is added here instead.
 * - **The honeypot field is dropped by name** (WWW-S024). It is a decoy only a
 *   bot fills, and the Salesforce intake is to read its mere presence as proof
 *   of one once SF-S092 ships — so posting the key even empty would reject a
 *   genuine lead. The exclusion is explicit rather than a side effect of the
 *   field being blank.
 * - Everything else sends its trimmed value and is omitted when blank.
 *
 * Note steps themselves are NOT disabled when off-screen — a multi-step form must
 * submit every step's answers, so only `[data-conditional]` sections get disabled.
 */
export function collectPayload(form: HTMLFormElement): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  const controls = form.querySelectorAll<FieldControl>("[name]");
  for (const control of controls) {
    const key = control.name;
    if (!key || control.disabled) continue;
    if (key === HONEYPOT_FIELD) continue;

    if (control instanceof HTMLInputElement && control.type === RADIO) {
      // Only the selected member contributes; an untouched group contributes
      // nothing, so the intake applies the field's own default.
      if (control.checked) payload[key] = radioValue(control);
      continue;
    }
    if (control instanceof HTMLInputElement && control.type === CHECKBOX) {
      payload[key] = control.checked;
      continue;
    }
    const value = control.value.trim();
    if (value === "") continue;
    payload[key] = control.hasAttribute("data-url")
      ? normalizeWebsite(value)
      : value;
  }
  return payload;
}

/**
 * Turn what an agency actually types into an absolute URL.
 *
 * "agencyname.com" and "www.agencyname.com" both become
 * "https://agencyname.com" / "https://www.agencyname.com"; an explicit scheme is
 * left alone, including http:// (we don't silently claim a site supports TLS).
 * Returns the trimmed input unchanged when there is nothing to prefix, so a
 * value we don't understand reaches Salesforce as typed rather than as a
 * mangled URL.
 */
export function normalizeWebsite(value: string): string {
  const trimmed = value.trim();
  if (trimmed === "") return "";
  if (/^[A-Za-z][A-Za-z0-9+.-]*:\/\//.test(trimmed)) return trimmed;
  // Protocol-relative ("//agencyname.com") is a URL missing only its scheme.
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  return `https://${trimmed}`;
}

/**
 * A radio's value as the intake wants it. `data-value-type="boolean"` maps
 * "true"/"false" onto real booleans, which is what the attestation control needs:
 * "I attest that all statements are accurate" must post `true`, not the string
 * "true". Everything else posts its string value.
 */
function radioValue(radio: HTMLInputElement): unknown {
  if (radio.dataset.valueType === "boolean") return radio.value === "true";
  return radio.value.trim();
}

/**
 * Apply live metadata to the form: rebuild each picklist's options (preserving
 * the leading "Select…" placeholder and the current selection when still valid)
 * and sync required/maxlength. Never removes or reorders form fields — the site
 * owns which fields exist.
 */
export function applyMetadata(
  form: HTMLFormElement,
  metadata: LeadFormMetadata,
): void {
  if (!metadata?.fields) return;
  for (const field of metadata.fields) {
    const control = form.querySelector<FieldControl>(`[name="${field.key}"]`);
    if (!control) continue;

    if (
      typeof field.maxLength === "number" &&
      (control instanceof HTMLInputElement ||
        control instanceof HTMLTextAreaElement)
    ) {
      control.maxLength = field.maxLength;
    }
    // Required is a server-authoritative hint; reflect it for native validation,
    // but never downgrade a field the page author explicitly marked required.
    if (field.required) control.required = true;

    if (field.picklistValues && control instanceof HTMLSelectElement) {
      applyOptions(control, field.picklistValues);
    }
    // A free-text control backed by a <datalist> (year of formation) gets the same
    // live values as a <select> would, so the typed value is checked against the
    // org's current list rather than a snapshot baked at build time.
    if (field.picklistValues && control instanceof HTMLInputElement) {
      applyDatalist(control, field.picklistValues);
    }
  }
}

function applyDatalist(
  input: HTMLInputElement,
  options: PicklistOption[],
): void {
  const list = input.ownerDocument.getElementById(
    input.getAttribute("list") ?? "",
  );
  if (!(list instanceof HTMLDataListElement)) return;
  list.replaceChildren();
  for (const opt of options) {
    const el = input.ownerDocument.createElement("option");
    el.value = opt.value;
    if (opt.label !== opt.value) el.textContent = opt.label;
    list.append(el);
  }
}

function applyOptions(
  select: HTMLSelectElement,
  options: PicklistOption[],
): void {
  const previous = select.value;
  // Keep a leading placeholder option (empty value) if the author included one.
  const placeholder =
    select.options.length > 0 && select.options[0].value === ""
      ? select.options[0]
      : null;
  select.replaceChildren();
  if (placeholder) select.append(placeholder);
  for (const opt of options) {
    const el = document.createElement("option");
    el.value = opt.value;
    el.textContent = opt.label;
    select.append(el);
  }
  // Restore the prior selection if it's still a valid option.
  if (previous && options.some((o) => o.value === previous)) {
    select.value = previous;
  } else if (placeholder) {
    select.value = "";
  }
}

/** POST the payload to the intake endpoint and normalize the outcome. */
export async function submitLead(
  url: string,
  payload: Record<string, unknown>,
  fetchImpl: typeof fetch,
): Promise<IntakeResult> {
  try {
    const res = await fetchImpl(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    let body: Partial<IntakeResult> = {};
    try {
      body = (await res.json()) as Partial<IntakeResult>;
    } catch {
      body = {};
    }
    return {
      success: Boolean(body.success) && res.ok,
      statusCode: res.status,
      leadId: body.leadId,
      submissionId: body.submissionId,
      errors: body.errors,
    };
  } catch {
    // Network failure — the submission never reached Salesforce.
    return { success: false, statusCode: 0, networkError: true };
  }
}

// --- DOM feedback ------------------------------------------------------------

function clearErrors(form: HTMLFormElement): void {
  form.querySelectorAll("[data-error-for]").forEach((el) => el.remove());
  form
    .querySelectorAll<FieldControl>("[aria-invalid='true']")
    .forEach((c) => c.removeAttribute("aria-invalid"));
}

function setStatus(doc: Document, message: string, tone: "error" | "ok"): void {
  const status = doc.getElementById("lead-form-status");
  if (!status) return;
  status.textContent = message;
  status.dataset.tone = tone;
  status.hidden = message === "";
}

function renderFieldErrors(form: HTMLFormElement, errors: IntakeError[]): void {
  for (const err of errors) {
    if (!err.field) continue;
    const control = form.querySelector<FieldControl>(`[name="${err.field}"]`);
    if (!control) continue;
    control.setAttribute("aria-invalid", "true");
    const msg = document.createElement("p");
    msg.className = "field-error text-sm text-www-error";
    msg.dataset.errorFor = err.field;
    msg.textContent = err.message;
    control.insertAdjacentElement("afterend", msg);
  }
}

/**
 * sessionStorage is not always reachable — Safari private mode throws on access
 * rather than returning null. Progress persistence is a nicety, so degrade to
 * "not kept" instead of breaking the form.
 */
function tryStorage(win?: Window): Storage | undefined {
  try {
    return win?.sessionStorage;
  } catch {
    return undefined;
  }
}

/**
 * Move to the step holding the first invalid control and re-report there, so the
 * browser's own message lands on a field the visitor can actually see.
 */
function revealFirstInvalidStep(
  form: HTMLFormElement,
  steps: StepsController,
): void {
  const invalid = Array.from(
    form.querySelectorAll<FieldControl>("[name]"),
  ).find((c) => !c.disabled && !c.checkValidity());
  if (!invalid) return;
  const step = invalid.closest<HTMLElement>("[data-step]");
  if (!step) return;
  const index = readSteps(form).indexOf(step);
  if (index >= 0) steps.goTo(index);
  invalid.reportValidity();
}

// --- Wiring ------------------------------------------------------------------

export interface InitOptions {
  fetchImpl?: typeof fetch;
  win?: Window;
  /** Injectable clock for the minimum-time guard (WWW-S024). */
  now?: () => number;
}

/**
 * Wire the pre-registration form: refresh options from live metadata, then submit
 * via fetch. No-op on pages without the form. Endpoint URLs are read from the
 * form's data-* attributes (baked at build from vite.config).
 */
export function initLeadForm(doc: Document, opts: InitOptions = {}): void {
  const form = doc.getElementById("lead-form");
  if (!(form instanceof HTMLFormElement)) return;

  const fetchImpl =
    opts.fetchImpl ?? doc.defaultView?.fetch.bind(doc.defaultView);
  const win = opts.win ?? doc.defaultView ?? undefined;
  const intakeUrl = form.dataset.intakeUrl ?? "";
  const metadataUrl = form.dataset.metadataUrl ?? "";
  if (!fetchImpl) return;

  // Runtime refresh: pull the latest options; on any failure the baked options
  // (already in the DOM) stand — the form is never left optionless.
  if (metadataUrl) {
    void fetchImpl(metadataUrl)
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((meta) => applyMetadata(form, meta as LeadFormMetadata))
      .catch(() => {
        /* keep baked options */
      });
  }

  // Multi-step wizard (WWW-S035). Returns null when the markup has no steps, so a
  // single-page form still works unchanged. sessionStorage keeps answers across an
  // accidental reload; it is cleared once the lead is safely in Salesforce.
  const storage = tryStorage(win);
  const steps = initSteps(form, storage ? { storage } : {});
  // US phone mask, matching the broker portal and PMS admin portal.
  initPhoneInputs(form);
  // Honeypot + minimum-time guards (WWW-S024). Wired up front because the timing
  // guard has to be listening before the visitor's first interaction.
  const guard = initSubmissionGuard(form, opts.now ? { now: opts.now } : {});

  /**
   * The success ending, factored out because the abuse guards borrow it: a
   * dropped submission must be INDISTINGUISHABLE from a real one, or a bot
   * author simply tunes until the page stops complaining.
   */
  const finishAsSuccess = (): void => {
    if (storage) clearProgress(storage);
    if (win) win.location.assign("/pre-register/thanks/");
  };

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    if (!intakeUrl) return;
    clearErrors(form);

    // Let the browser surface native validity (required, type=email, etc.).
    // On a stepped form the offending control may be on a step that is currently
    // hidden, where the browser cannot show its message — so jump there first,
    // otherwise submission fails silently with nothing for the visitor to fix.
    if (!form.reportValidity()) {
      if (steps) revealFirstInvalidStep(form, steps);
      return;
    }
    // The 100% rule spans three inputs, so it is not expressible as native
    // validity on any single one.
    if (!percentagesValid(form)) {
      if (steps) revealFirstInvalidStep(form, steps);
      return;
    }

    // Abuse guards LAST, after validation: a false positive here costs a real
    // lead, and running the guards first would also hand a visitor with an
    // incomplete form a thank-you page instead of the field they must fix.
    // Everything past this point is a submission we believe in.
    if (guard.shouldDrop()) {
      finishAsSuccess();
      return;
    }

    const submitBtn = form.querySelector<HTMLButtonElement>(
      "button[type='submit']",
    );
    const payload = collectPayload(form);

    if (submitBtn) submitBtn.disabled = true;
    setStatus(doc, "Sending…", "ok");

    void submitLead(intakeUrl, payload, fetchImpl).then((result) => {
      if (result.success) {
        // Only discard saved answers once the lead is actually in Salesforce —
        // on any failure they stay, so a retry does not start from an empty
        // form. Then land on the styled thank-you page, as before.
        finishAsSuccess();
        return;
      }
      if (submitBtn) submitBtn.disabled = false;

      if (result.errors && result.errors.some((e) => e.field)) {
        renderFieldErrors(form, result.errors);
        setStatus(
          doc,
          "Please correct the highlighted fields and try again.",
          "error",
        );
      } else {
        setStatus(
          doc,
          "Sorry — we couldn't send your message just now. Please try again, or email support@signalspecialty.com.",
          "error",
        );
      }
    });
  });
}
